// Shared helpers/types for the Catalog Manager product & brand image manager.
//
// Images are linked by CONVENTION (no data writes): a product image is keyed by
// the food id, a brand image by a heavily-sanitized slug of the free-form brand
// name so most spellings of the same brand collapse to one key.

/**
 * Collapse a free-form brand name to a stable lookup slug. Mirrors the Go
 * sanitizeBrandKey in api/products/service/catalog_images.go and MUST stay in
 * sync with it: NFKD-normalize, drop combining marks (accents), lowercase, and
 * keep only [a-z0-9]. e.g. "Trader Joe's®", "trader joes", and "TRADER JOE'S"
 * all become "traderjoes".
 */
export const sanitizeBrandKey = (name: string): string =>
  (name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // combining diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** Accept attribute for the file inputs (matches the server's allowed types). */
export const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

/** Client-side size cap; mirrors the server's maxCatalogImageBytes (10 MB). */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/** Result of an image upload (returned by the products image endpoints). */
export interface CatalogImageResult {
  url: string;
  bucket: string;
  path: string;
  key: string;
  brandName?: string;
}

/** Result of a batch image lookup: only keys that HAVE an image are present. */
export interface CatalogImageLookup {
  /** productId -> public image url */
  products: Record<string, string>;
  /** brandName (as supplied) -> public image url */
  brands: Record<string, string>;
}

/**
 * Validate a chosen/dropped file before upload. Returns an error string, or ""
 * when the file is an acceptable image within the size cap.
 */
export const validateImageFile = (file: File): string => {
  if (!file.type.startsWith("image/")) return "Please choose an image file";
  if (file.size > MAX_IMAGE_BYTES) return "Image is too large (max 10 MB)";
  return "";
};
